/**
 * Role-aware navigation (§3 → §16.2 consoles). Visibility is a permission question answered by `can()` from
 * contracts; a sponsor viewer holds only `sponsor.report.view` and therefore sees only Sponsor reporting.
 */
import type { Permission, StaffRole, StaffSessionDto } from '@windrop/contracts';
import { sessionCan } from '../auth/session';

export interface NavItem {
  id: string;
  label: string;
  to: string;
  /** ANY of these permissions shows the item. */
  permissions?: readonly Permission[];
  /** ANY of these roles shows the item (for areas without a dedicated permission, e.g. staff settings). */
  roles?: readonly StaffRole[];
  /** Section grouping in the sidebar. */
  section: 'operate' | 'catalogue' | 'insight' | 'admin';
  /** Keyboard shortcut (g then key). */
  shortcut?: string;
}

export const NAV_ITEMS: readonly NavItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    to: '/',
    permissions: ['lottery.read'],
    section: 'operate',
    shortcut: 'd',
  },
  {
    id: 'lotteries',
    label: 'Lotteries',
    to: '/lotteries',
    permissions: ['lottery.read'],
    section: 'operate',
    shortcut: 'l',
  },
  {
    id: 'draws',
    label: 'Draw control room',
    to: '/draws',
    permissions: ['draw.precheck', 'draw.run', 'draw.reveal', 'draw.audit.read'],
    section: 'operate',
    shortcut: 'r',
  },
  {
    id: 'winners',
    label: 'Winners board',
    to: '/winners',
    permissions: ['winners.view'],
    section: 'operate',
    shortcut: 'w',
  },
  {
    id: 'claims',
    label: 'Claims queue',
    to: '/claims',
    permissions: ['claims.view'],
    section: 'operate',
    shortcut: 'c',
  },
  {
    id: 'support',
    label: 'Support cases',
    to: '/support',
    permissions: ['claims.view'],
    section: 'operate',
  },
  {
    id: 'finance',
    label: 'Finance',
    to: '/finance',
    permissions: ['finance.ledger.read', 'finance.reconcile', 'finance.refund', 'finance.payout.approve'],
    section: 'operate',
    shortcut: 'f',
  },
  {
    id: 'fulfillment',
    label: 'Fulfillment',
    to: '/fulfillment',
    permissions: ['fulfillment.update'],
    section: 'operate',
  },
  {
    id: 'audience',
    label: 'Audience',
    to: '/audience',
    permissions: ['audience.segments.read'],
    section: 'insight',
    shortcut: 'u',
  },
  {
    id: 'catalog',
    label: 'Sponsors & prizes',
    to: '/catalog',
    permissions: ['prizes.manage', 'sponsors.manage', 'brands.manage'],
    section: 'catalogue',
    shortcut: 'p',
  },
  {
    id: 'sponsor-reports',
    label: 'Sponsor reporting',
    to: '/sponsor-reports',
    permissions: ['sponsor.report.view'],
    section: 'insight',
    shortcut: 's',
  },
  {
    id: 'audit',
    label: 'Audit & incidents',
    to: '/audit',
    permissions: ['audit.read'],
    section: 'insight',
    shortcut: 'a',
  },
  {
    id: 'settings',
    label: 'Settings & staff',
    to: '/settings',
    roles: ['ORGANIZER_ADMIN', 'PLATFORM_SUPER_ADMIN'],
    section: 'admin',
  },
];

export const SECTION_LABELS: Record<NavItem['section'], string> = {
  operate: 'Operate',
  catalogue: 'Catalogue',
  insight: 'Insight',
  admin: 'Administration',
};

export const isNavItemVisible = (item: NavItem, session: StaffSessionDto | null): boolean => {
  if (!session) return false;
  const byPermission = item.permissions?.some((p) => sessionCan(session, p)) ?? false;
  const byRole = item.roles?.some((r) => session.roles.includes(r)) ?? false;
  return byPermission || byRole;
};

export const visibleNav = (session: StaffSessionDto | null): NavItem[] =>
  NAV_ITEMS.filter((item) => isNavItemVisible(item, session));

/** Where `/` should land for this session: the dashboard when allowed, else the first visible console. */
export const homePath = (session: StaffSessionDto | null): string => {
  const items = visibleNav(session);
  if (items.some((i) => i.id === 'dashboard')) return '/';
  return items[0]?.to ?? '/forbidden';
};
